import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Mail, Activity, Users, TrendingUp, AlertCircle, Search, Filter, Download, Calendar, Building2, MapPin } from 'lucide-react';
import { BASE_URL } from '../../../services/configUrls';


const StaffDashboard = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('counting');
  const [error, setError] = useState(null);
  
  // Date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');


  // Function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };


  // Function to format date for display
  const formatDateForDisplay = (date) => {
    return date;
  };


  // Initialize dates to current day by default
  useEffect(() => {
    const currentDate = getCurrentDate();
    setStartDate(currentDate);
    setEndDate(currentDate);
  }, []);


  // Fetch data when dates change
  useEffect(() => {
    if (startDate && endDate) {
      fetchDashboardData();
    }
  }, [startDate, endDate]);


  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const dateData = {
        started: startDate,
        ended: endDate
      };


      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/allStaffDashboard`,
        headers: { 
          'Content-Type': 'application/json'
        },
        data: dateData
      };


      const response = await axios.request(config);
      console.log('Dashboard Data:', response.data);
      
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setDashboardData(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        // In case the API returns data wrapped in an object
        setDashboardData(response.data.data);
      } else {
        console.warn('Unexpected data format:', response.data);
        setDashboardData([]);
        setError('Received unexpected data format from server');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.response?.data?.message || 'Failed to fetch dashboard data');
      setDashboardData([]);
      setLoading(false);
    }
  };


  // Process data - group by staff_user_id and sum counting values
  const processedData = () => {
    // Safety check: ensure dashboardData is an array
    if (!Array.isArray(dashboardData) || dashboardData.length === 0) {
      return [];
    }

    try {
      const staffGroups = dashboardData.reduce((acc, item) => {
        // Skip items without required fields
        if (!item || !item.staff_user_id || typeof item.counting === 'undefined') {
          console.warn('Skipping invalid item:', item);
          return acc;
        }

        const staffId = item.staff_user_id;

        if (acc[staffId]) {
          // Add to existing staff member's count
          acc[staffId].counting += Number(item.counting) || 0;
        } else {
          // Create new entry for this staff member
          acc[staffId] = {
            staff_user_id: staffId,
            solved_by_full_name: item.solved_by_full_name || '',
            solved_by_email: item.solved_by_email || '',
            rm_email: item.rm_email || '',
            institute_name: item.institute_name || '',
            state_name: item.state_name || '',
            counting: Number(item.counting) || 0
          };
        }
        return acc;
      }, {});


      return Object.values(staffGroups).map(item => {
        // Determine display name: full name > email > user ID
        let displayName = item.solved_by_full_name?.trim() || item.solved_by_email || `User ${item.staff_user_id}`;
        
        return {
          staff_user_id: item.staff_user_id,
          displayName: displayName.length > 30 ? `${displayName.substring(0, 30)}...` : displayName,
          fullDisplayName: displayName,
          solved_by_full_name: item.solved_by_full_name,
          solved_by_email: item.solved_by_email,
          rm_email: item.rm_email,
          institute_name: item.institute_name,
          state_name: item.state_name,
          counting: item.counting
        };
      });
    } catch (error) {
      console.error('Error processing data:', error);
      return [];
    }
  };


  // Calculate statistics
  const getStatistics = () => {
    const processedStaff = processedData();
    const totalStaff = processedStaff.length;
    const totalActivities = processedStaff.reduce((sum, item) => sum + (item.counting || 0), 0);
    const avgPerStaff = totalStaff > 0 ? (totalActivities / totalStaff) : 0;
    const maxActivity = processedStaff.length > 0 
      ? Math.max(...processedStaff.map(item => item.counting || 0), 0)
      : 0;
    
    // Get unique institutes and states
    const uniqueInstitutes = new Set(processedStaff.map(item => item.institute_name).filter(Boolean)).size;
    const uniqueStates = new Set(processedStaff.map(item => item.state_name).filter(Boolean)).size;
    
    return { 
      totalStaff, 
      totalActivities, 
      avgPerStaff, 
      maxActivity,
      uniqueInstitutes,
      uniqueStates
    };
  };


  // Filter and sort data
  const getFilteredData = () => {
    let filtered = processedData();
    
    if (!Array.isArray(filtered)) {
      return [];
    }
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        (item.fullDisplayName && item.fullDisplayName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.solved_by_email && item.solved_by_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.institute_name && item.institute_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.state_name && item.state_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.staff_user_id && item.staff_user_id.toString().includes(searchTerm))
      );
    }
    
    return filtered.sort((a, b) => {
      if (sortBy === 'counting') return (b.counting || 0) - (a.counting || 0);
      if (sortBy === 'name') return (a.fullDisplayName || '').localeCompare(b.fullDisplayName || '');
      if (sortBy === 'institute') return (a.institute_name || '').localeCompare(b.institute_name || '');
      return 0;
    });
  };


  // Prepare pie chart data
  const getPieChartData = () => {
    const data = getFilteredData();
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899'];
    
    return data.slice(0, 8).map((item, index) => ({
      ...item,
      color: colors[index % colors.length]
    }));
  };


  // Quick date preset functions
  const setToday = () => {
    const currentDate = getCurrentDate();
    setStartDate(currentDate);
    setEndDate(currentDate);
  };


  const setYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    setStartDate(yesterdayStr);
    setEndDate(yesterdayStr);
  };


  const setLast7Days = () => {
    const end = getCurrentDate();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    const startStr = start.toISOString().split('T')[0];
    setStartDate(startStr);
    setEndDate(end);
  };


  const setLast30Days = () => {
    const end = getCurrentDate();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    const startStr = start.toISOString().split('T')[0];
    setStartDate(startStr);
    setEndDate(end);
  };


  const setThisMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = getCurrentDate();
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end);
  };


  const setCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const start = new Date(year, month, 1).toISOString().split('T')[0];
    const end = new Date(year, month + 1, 0).toISOString().split('T')[0];
    setStartDate(start);
    setEndDate(end);
  };


  const statistics = getStatistics();


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <div className="text-sm font-medium text-gray-700">Loading dashboard...</div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Support Dashboard</h1>
            <p className="text-xs text-gray-600 mt-1">Monitor staff activity and support metrics</p>
          </div>
          <div className="flex space-x-3">
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
              <Download className="w-3 h-3 mr-2" />
              Export
            </button>
            <button 
              onClick={fetchDashboardData}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
            >
              <Activity className="w-3 h-3 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>


      <div className="px-4 py-4 space-y-4">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <div>
                <h3 className="text-sm font-semibold text-red-900">Error Loading Data</h3>
                <p className="text-xs text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Date Filter Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center mb-3">
            <Calendar className="w-4 h-4 text-blue-600 mr-2" />
            <h3 className="text-sm font-semibold text-gray-900">Date Range Filter</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
              />
            </div>
            <div className="md:col-span-2 lg:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-2">Quick Presets</label>
              <div className="flex flex-wrap gap-1">
                <button 
                  onClick={setToday}
                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs hover:bg-blue-200 transition-colors"
                >
                  Today
                </button>
                <button 
                  onClick={setYesterday}
                  className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200 transition-colors"
                >
                  Yesterday
                </button>
                <button 
                  onClick={setLast7Days}
                  className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs hover:bg-green-200 transition-colors"
                >
                  Last 7 Days
                </button>
                <button 
                  onClick={setLast30Days}
                  className="px-2 py-1 bg-orange-100 text-orange-700 rounded-lg text-xs hover:bg-orange-200 transition-colors"
                >
                  Last 30 Days
                </button>
                <button 
                  onClick={setCurrentMonth}
                  className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs hover:bg-purple-200 transition-colors"
                >
                  This Month
                </button>
              </div>
            </div>
          </div>
          
          <div className="text-xs text-gray-600">
            Showing data from <span className="font-semibold">{formatDateForDisplay(startDate)}</span> to <span className="font-semibold">{formatDateForDisplay(endDate)}</span>
          </div>
        </div>


        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Total Staff</p>
                <p className="text-sm font-bold text-gray-900">{statistics.totalStaff}</p>
              </div>
            </div>
          </div>


          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Activity className="w-4 h-4 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Total Activities</p>
                <p className="text-sm font-bold text-gray-900">{statistics.totalActivities}</p>
              </div>
            </div>
          </div>


          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="w-4 h-4 text-orange-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Average per Staff</p>
                <p className="text-sm font-bold text-gray-900">{statistics.avgPerStaff.toFixed(1)}</p>
              </div>
            </div>
          </div>


          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <AlertCircle className="w-4 h-4 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Highest Activity</p>
                <p className="text-sm font-bold text-gray-900">{statistics.maxActivity}</p>
              </div>
            </div>
          </div>
        </div>


        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                <input
                  type="text"
                  placeholder="Search staff, institute, state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-7 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs w-64"
                />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <Filter className="w-3 h-3 text-gray-500" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                >
                  <option value="counting">Sort by Activity</option>
                  <option value="name">Sort by Name</option>
                  <option value="institute">Sort by Institute</option>
                </select>
              </div>
            </div>
          </div>
        </div>


        {/* Charts Section */}
        {getFilteredData().length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Vertical Bar Chart */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Staff Activity Distribution</h3>
                <div className="text-xs text-gray-500">
                  {getFilteredData().length} staff shown
                </div>
              </div>
              
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={getFilteredData()}
                  margin={{ top: 10, right: 20, left: 10, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="displayName" 
                    stroke="#64748b" 
                    fontSize={9}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    interval={0}
                  />
                  <YAxis 
                    stroke="#64748b"
                    fontSize={10}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      fontSize: '11px'
                    }}
                    formatter={(value, name, props) => [
                      value,
                      'Activities'
                    ]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0]) {
                        const item = payload[0].payload;
                        return (
                          <div>
                            <div className="font-semibold">{item.fullDisplayName}</div>
                            <div className="text-xs text-gray-600">User ID: {item.staff_user_id}</div>
                            <div className="text-xs text-gray-600">{item.solved_by_email}</div>
                          </div>
                        );
                      }
                      return label;
                    }}
                  />
                  <Bar 
                    dataKey="counting" 
                    fill="#3B82F6" 
                    name="Activities"
                    radius={[4, 4, 0, 0]}
                    barSize={30}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>


            {/* Pie Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Top 8 Contributors</h3>
              
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={getPieChartData()}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    dataKey="counting"
                    label={({counting, percent}) => `${counting} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                    fontSize={9}
                  >
                    {getPieChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      fontSize: '11px'
                    }}
                    formatter={(value, name, props) => [
                      value,
                      'Activities'
                    ]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0]) {
                        const item = payload[0].payload;
                        return `${item.fullDisplayName} (ID: ${item.staff_user_id})`;
                      }
                      return label;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Legend */}
              <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                {getPieChartData().map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-xs">
                    <div className="flex items-center">
                      <div 
                        className="w-2 h-2 rounded-full mr-2" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-gray-700 truncate max-w-[120px]">
                        {item.displayName}
                      </span>
                    </div>
                    <span className="font-semibold text-gray-900">{item.counting}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-gray-900 mb-1">No Data Available</h3>
            <p className="text-xs text-gray-600">
              {searchTerm 
                ? 'No staff members match your search criteria' 
                : 'No activity data found for the selected date range'}
            </p>
          </div>
        )}


        {/* Data Table */}
        {getFilteredData().length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Staff Activity Details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Staff Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institute
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      State
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Activities
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredData().map((item, index) => (
                    <tr key={`${item.staff_user_id}-${index}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="w-3 h-3 text-blue-600" />
                          </div>
                          <div className="ml-2 text-xs font-semibold text-gray-900">
                            #{item.staff_user_id}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-900">
                          {item.solved_by_full_name || <span className="italic text-gray-400">No Name</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <Mail className="w-3 h-3 text-gray-400 mr-2 flex-shrink-0" />
                          <div className="text-xs text-gray-600 truncate max-w-[200px]">
                            {item.solved_by_email}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <Building2 className="w-3 h-3 text-gray-400 mr-2 flex-shrink-0" />
                          <div className="text-xs text-gray-600 max-w-[200px]">
                            {item.institute_name || <span className="italic text-gray-400">N/A</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <MapPin className="w-3 h-3 text-gray-400 mr-2" />
                          <div className="text-xs text-gray-600">
                            {item.state_name || <span className="italic text-gray-400">N/A</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-xs text-gray-900 font-bold mr-2">
                            {item.counting}
                          </div>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all" 
                              style={{ 
                                width: `${statistics.maxActivity > 0 ? (item.counting / statistics.maxActivity) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          {statistics.totalActivities > 0 
                            ? ((item.counting / statistics.totalActivities) * 100).toFixed(1)
                            : 0}%
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default StaffDashboard;
